import { Component, Input, OnChanges, SimpleChanges } from "@angular/core";
import { SafeHtml } from "@angular/platform-browser";
import { BaseComponent } from "../common/base.component";
import { LabelViewerComponent } from "../common/label-viewer/label-viewer.component";
import { GameItem } from "../models/game-item.models";
import { Rarity } from "../models/enums";
import { SimulatorResult, SimulatorService } from "../services/simulator.service";
import { NavigationService } from "../services/navigation.service";
import { ItemLabelComponent } from "../common/item-label/item-label.component";
import { ItemDataService } from "../services/item-data.service";
import { SimulatorResultSummaryComponent } from "../common/simulator-result-summary/simulator-result-summary.component";
import { ExplainerService } from "../services/explainer.service";
import { getGameItemExampleName, getGameItemSocketsDisplay } from "../services/game-item-display.util";
// import { JsonPipe } from "@angular/common";

@Component({
  selector: 'app-game-item-viewer',
  templateUrl: './game-item-viewer.component.html',
  styleUrls: ['./game-item-viewer.component.scss'],
  imports: [LabelViewerComponent, ItemLabelComponent, SimulatorResultSummaryComponent],
})
export class GameItemViewerComponent extends BaseComponent implements OnChanges {
  @Input({ required: true }) pastedItemText!: string;
  @Input({ required: true }) gameItem!: GameItem;
  @Input({ required: true }) simulatorResult!: SimulatorResult;

  isShownByDefaultAsGear = false;
  isShownByDefaultAsJewellery = false;
  isGear = false;
  isJewellery = false;

  holdAltToggle = false;
  exampleName = '';
  socketsDisplay: string | undefined;

  ruleExplainers: SafeHtml[] = [];
  uniqueExplainer: SafeHtml | null = null;
  hasRuleExplainers = false;

  constructor(
    private navigationService: NavigationService,
    private simulatorService: SimulatorService,
    private itemDataService: ItemDataService,
    private explainerService: ExplainerService,
  ) {
    super();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['gameItem'] || changes['simulatorResult']) {
      if (changes['gameItem']) {
        this.exampleName = getGameItemExampleName(this.gameItem);
        this.socketsDisplay = getGameItemSocketsDisplay(this.gameItem);
      }
      const equipmentRarities: Rarity[] = [Rarity.Normal, Rarity.Magic, Rarity.Rare];
      const shownByDefault = this.simulatorResult?.noMatch === true
        && !!this.gameItem.rarity && equipmentRarities.includes(this.gameItem.rarity as Rarity);

      this.isJewellery = this.itemDataService.isJewelleryType(this.gameItem.itemClass);
      this.isGear = !this.isJewellery && this.itemDataService.getAllGearTypes().includes(this.gameItem.itemClass);
      this.isShownByDefaultAsJewellery = shownByDefault && this.isJewellery;
      this.isShownByDefaultAsGear = shownByDefault && this.isGear;
      this.updateExplainers();
    }
  }

  hidePasteRuleExplainers(): void {
    this.settings.dismissPasteRuleExplainers();
  }

  hidePasteUniqueExplainers(): void {
    this.settings.dismissPasteUniqueExplainers();
  }

  private updateExplainers(): void {
    this.ruleExplainers = this.explainerService.getRuleExplainers(this.simulatorResult?.rule?.completeId);
    this.hasRuleExplainers = this.ruleExplainers.length > 0;
    const isIdentifiedUnique = this.gameItem?.rarity === 'Unique' && this.gameItem.identified !== false && !!this.gameItem.name;
    this.uniqueExplainer = isIdentifiedUnique
      ? this.explainerService.getUniqueExplainer(this.gameItem.name)
      : null;
  }

  toggleHoldAlt(): void {
    this.holdAltToggle = !this.holdAltToggle;
  }

  goToRule(completeId: string) {
    this.navigationService.navigateToFilterTitle(completeId, this.gameItem.baseType);
  }

  goToOutput(completeId: string) {
    this.navigationService.navigateToFilterOutput(completeId, true);
  }

  goToRemainingGear() {
    this.navigationService.navigateToFilterTitle('gear-hide-remaining');
  }

  goToRemainingJewellery() {
    const ruleIdByClass: Record<string, string> = {
      Amulets: 'jewellery-hidden-amulets-normal',
      Rings: 'jewellery-hidden-rings-normal',
      Belts: 'jewellery-hidden-belts-normal',
    };
    this.navigationService.navigateToFilterTitle(ruleIdByClass[this.gameItem.itemClass] ?? 'jewellery-hidden-rings-normal');
  }

  updateUnidentifiedItemTier(unidentifiedItemTier: number) {
    this.gameItem = new GameItem({ ...this.gameItem, unidentifiedItemTier: unidentifiedItemTier });
    this.simulatorResult = this.simulatorService.simulate(this.gameItem);
    this.updateExplainers();
  }
}
